import React from 'react';
import { Provider } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { isEmpty } from 'lodash';
import { persistor, store } from './redux/store';
import Global from './components/Global';
import MigrationPopup from './components/MigrationPopup';

import Login from './screens/auth/Login';
import ChangePassword from './screens/auth/ChangePassword';
import ForgotPassword from './screens/auth/ForgotPassword';

import SelectPhoto from './screens/SelectPhoto';
import Review from './screens/Review';
import Success from './screens/notify/Success';
import CustomerInfo from './screens/CustomerInfo';
import MissingEmail from './screens/notify/MissingEmail';
import Profile from './screens/Profile';
import ReferralInfo from './screens/ReferralInfo';
import Loader from './components/Loader';
import AddVehicle from './screens/dashboard/AddVehicle';
import ChooseVehicle from './screens/dashboard/ChooseVehicle';
import Referrals from './screens/dashboard/Referrals';
import SuccessReferral from './screens/notify/SuccessReferral';
import withSplashScreen from './screens/SplashScreen';
import Sales from './screens/dashboard/Sales';
import SelectVideo from './screens/SelectVideo';
import SuccessTestimonial from './screens/notify/SuccessTestimonial';
import ScanQR from './screens/dashboard/ScanQR';
import RedemptionHistory from './screens/dashboard/RedemptionHistory';

const ValidateRoute = ({ children }) => {
  const { user } = store.getState();

  if (!isEmpty(user?.id) && user?.role?.includes('sales')) {
    return <Navigate to="/inventory" replace />;
  }

  if (!isEmpty(user?.id) && !user?.role?.includes('sales')) {
    return <Navigate to="/redemption-history" replace />;
  }

  return children;
};

const RoleRoute = ({ children, allow = [] }) => {
  const { user } = store.getState();

  if (!isEmpty(user?.id) && allow.some((role) => user?.role?.includes(role))) {
    return children;
  }

  return <Navigate to="/login" replace />;
};

const AppRoutes = () => (
  <Provider store={store}>
    <PersistGate loading={<Loader />} persistor={persistor}>
      <Global store={store} />
      <MigrationPopup />
      <Routes>
        {/* Public Auth Routes */}
        <Route
          index
          path="/login"
          element={
            <ValidateRoute>
              <Login />
            </ValidateRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <ValidateRoute>
              <ForgotPassword />
            </ValidateRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ValidateRoute>
              <ChangePassword />
            </ValidateRoute>
          }
        />

        {/* Protected Routes (sales only) */}
        <Route
          path="/inventory"
          element={
            <RoleRoute allow={['sales']}>
              <ChooseVehicle />
            </RoleRoute>
          }
        />
        <Route
          path="/referrals"
          element={
            <RoleRoute allow={['sales']}>
              <Referrals />
            </RoleRoute>
          }
        />
        <Route
          path="/add-vehicle"
          element={
            <RoleRoute allow={['sales']}>
              <AddVehicle />
            </RoleRoute>
          }
        />
        <Route
          path="/owners"
          element={
            <RoleRoute allow={['sales']}>
              <Sales />
            </RoleRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <RoleRoute allow={['sales']}>
              <Profile />
            </RoleRoute>
          }
        />
        <Route
          path="/select-photo"
          element={
            <RoleRoute allow={['sales']}>
              <SelectPhoto />
            </RoleRoute>
          }
        />
        <Route
          path="/select-video"
          element={
            <RoleRoute allow={['sales']}>
              <SelectVideo />
            </RoleRoute>
          }
        />
        <Route
          path="/review"
          element={
            <RoleRoute allow={['sales']}>
              <Review />
            </RoleRoute>
          }
        />
        <Route
          path="/customer-info"
          element={
            <RoleRoute allow={['sales']}>
              <CustomerInfo />
            </RoleRoute>
          }
        />
        <Route
          path="/referral-info"
          element={
            <RoleRoute allow={['sales']}>
              <ReferralInfo />
            </RoleRoute>
          }
        />
        <Route
          path="/missing-email"
          element={
            <RoleRoute allow={['sales']}>
              <MissingEmail />
            </RoleRoute>
          }
        />
        <Route
          path="/success"
          element={
            <RoleRoute allow={['sales']}>
              <Success />
            </RoleRoute>
          }
        />
        <Route
          path="/success-referral"
          element={
            <RoleRoute allow={['sales']}>
              <SuccessReferral />
            </RoleRoute>
          }
        />
        <Route
          path="/success-testimonial"
          element={
            <RoleRoute allow={['sales']}>
              <SuccessTestimonial />
            </RoleRoute>
          }
        />

        {/* Admin/Manager only */}
        <Route
          path="/scan-qr"
          element={
            <RoleRoute allow={['sales', 'service_manager', 'service_advisor']}>
              <ScanQR />
            </RoleRoute>
          }
        />
        <Route
          path="/redemption-history"
          element={
            <RoleRoute allow={['sales', 'service_manager', 'service_advisor']}>
              <RedemptionHistory />
            </RoleRoute>
          }
        />

        <Route path="/" element={<Navigate to="/login" />} />
      </Routes>
    </PersistGate>
  </Provider>
);

export default withSplashScreen(AppRoutes);
